import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import toast from "react-hot-toast";
import {
  ArrowRightIcon,
  BellAlertIcon,
  BuildingOffice2Icon,
  CheckCircleIcon,
  ClipboardDocumentCheckIcon,
  ClockIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  EyeIcon,
  PrinterIcon,
} from "@heroicons/react/24/outline";
import { HsePageChrome } from "../../../components/dashboard/hse/HsePageChrome";
import { fetchOrgCharts, exportOrgChartRevisionPDF } from "../../../api/quality/orgCharts";
import { fetchHseDashboard } from "../../../api/hse/dashboard";
import { ResourceLoading } from "../../../components/utils/ResourceLoading";
import { ResourceError } from "../../../components/utils/ResourceError";
import { PMDialog } from "../../../components/utils/PMDialog";
import { CalculationInfoIcon } from "../../../components/dashboard/accounting/CalculationInfoIcon";
import { EMPTY_HSE_DASHBOARD } from "../../../components/dashboard/hse/hseDashboardUtils";
import { HseSetupMissingBanner } from "../../../components/dashboard/hse/HseSetupMissingBanner";

const KPI_CARDS = [
  {
    id: "events",
    href: "/dashboard/hse-ks/events",
    icon: ExclamationTriangleIcon,
    iconWrap: "bg-sky-100 text-sky-700",
    info: false,
  },
  {
    id: "deviations",
    href: "/dashboard/hse-ks/deviations",
    icon: ClipboardDocumentCheckIcon,
    iconWrap: "bg-amber-100 text-amber-800",
    info: false,
  },
  {
    id: "unread_docs",
    href: "/dashboard/quality/employee-handbook",
    icon: DocumentTextIcon,
    iconWrap: "bg-indigo-100 text-indigo-700",
    info: true,
  },
  {
    id: "open_actions",
    href: "/dashboard/hse-ks/tiltak",
    icon: CheckCircleIcon,
    iconWrap: "bg-emerald-100 text-emerald-700",
    info: true,
  },
  {
    id: "overdue",
    href: "/dashboard/hse-ks/tiltak",
    icon: ClockIcon,
    iconWrap: "bg-rose-100 text-rose-700",
    info: true,
  },
];

const ALERT_STYLES = {
  error: "border-rose-200 bg-rose-50 text-rose-900",
  warning: "border-amber-200 bg-amber-50 text-amber-900",
  info: "border-sky-200 bg-sky-50 text-sky-900",
};

function formatDate(value, language) {
  if (!value) return "";
  const parsed = new Date(value);
  if (!Number.isFinite(parsed.getTime())) return "";
  return parsed.toLocaleDateString(language || undefined);
}

function SectionCard({ title, action, children }) {
  return (
    <section className="rounded-2xl border border-slate-200 bg-white shadow-[0_1px_2px_rgba(0,0,0,.04),0_6px_22px_rgba(0,0,0,.07)]">
      <div className="flex items-center justify-between gap-3 border-b border-slate-100 px-4 py-3 sm:px-5">
        <h2 className="text-base font-semibold text-slate-950">{title}</h2>
        {action}
      </div>
      <div className="px-4 py-4 sm:px-5">{children}</div>
    </section>
  );
}

function SeeAllLink({ to, label }) {
  return (
    <Link
      to={to}
      className="inline-flex items-center text-sm font-medium text-blue-600 hover:text-blue-800"
    >
      {label}
      <ArrowRightIcon className="ml-1 h-3.5 w-3.5" />
    </Link>
  );
}

function EmptyLine({ text }) {
  return <p className="text-sm text-slate-500">{text}</p>;
}

/**
 * HSE/KS Dashboard — HMS home
 * KPI, mine oppgaver, varsler, nylige hendelser.
 */
export const HseKsDashboard = () => {
  const { t, i18n } = useTranslation();
  const currentUser = useSelector((state) => state.session.currentUser);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [dashboard, setDashboard] = useState(EMPTY_HSE_DASHBOARD);
  const [infoKpi, setInfoKpi] = useState(null);
  const [orgCharts, setOrgCharts] = useState([]);
  const [loadingOrgCharts, setLoadingOrgCharts] = useState(true);

  const loadDashboard = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const result = await fetchHseDashboard({ currentUserId: currentUser?.id });
      setDashboard(result.data || EMPTY_HSE_DASHBOARD);
      if (result.failed) {
        toast.error(t("hse.dashboard.error_toast"));
      }
      return result;
    } catch (err) {
      console.error("Error fetching HSE dashboard", err);
      setError(true);
      toast.error(t("hse.dashboard.error_toast"));
      return null;
    } finally {
      setLoading(false);
    }
  }, [currentUser?.id, t]);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      setError(false);
      try {
        const result = await fetchHseDashboard({ currentUserId: currentUser?.id });
        if (cancelled) return;
        setDashboard(result.data || EMPTY_HSE_DASHBOARD);
        if (result.failed) {
          toast.error(t("hse.dashboard.error_toast"));
        }
      } catch (err) {
        console.error("Error fetching HSE dashboard", err);
        if (cancelled) return;
        setError(true);
        toast.error(t("hse.dashboard.error_toast"));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [currentUser?.id, t]);

  useEffect(() => {
    let cancelled = false;
    const loadOrgCharts = async () => {
      try {
        setLoadingOrgCharts(true);
        const response = await fetchOrgCharts({ per_page: 5, page: 1 });
        if (!cancelled) setOrgCharts(response.data || []);
      } catch (err) {
        console.error("Error fetching org charts:", err);
      } finally {
        if (!cancelled) setLoadingOrgCharts(false);
      }
    };
    loadOrgCharts();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleExportPDF = async (orgChartId, revisionId) => {
    try {
      const response = await exportOrgChartRevisionPDF(orgChartId, revisionId);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `organisasjonskart-revisjon-${revisionId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(t("hse.dashboard.org_charts.pdf_success"));
    } catch (err) {
      toast.error(t("hse.dashboard.org_charts.pdf_error"));
    }
  };

  const approvedCharts = orgCharts.filter((oc) => oc.approved_revision).slice(0, 3);
  const kpis = dashboard.kpis || EMPTY_HSE_DASHBOARD.kpis;

  const alertLabel = (alert) => {
    if (alert.title === "overdue_actions") {
      return t("hse.dashboard.alerts.overdue_actions", { count: alert.count });
    }
    if (alert.title === "unread_docs") {
      return t("hse.dashboard.alerts.unread_docs", { count: alert.count });
    }
    if (alert.title === "due_soon") {
      return t("hse.dashboard.alerts.due_soon", { count: alert.count });
    }
    if (alert.title === "critical_events") {
      return t("hse.dashboard.alerts.critical_events", { count: alert.count });
    }
    return alert.title;
  };

  return (
    <HsePageChrome
      title={t("hse.dashboard.title")}
      subtitle={t("hse.dashboard.subtitle")}
    >
      <div className="mx-auto mt-8 max-w-7xl px-4 pb-10 sm:px-6">
        <HseSetupMissingBanner />
        {error ? (
          <ResourceError
            errors={[t("hse.dashboard.error")]}
            onRetry={loadDashboard}
            retryLabel={t("hse.dashboard.retry")}
          />
        ) : (
          <div className="space-y-6">
            <section>
              <div className="mb-3 flex items-center justify-between">
                <h2 className="text-sm font-semibold uppercase tracking-[0.14em] text-slate-500">
                  {t("hse.dashboard.kpi.title")}
                </h2>
              </div>
              {loading ? (
                <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
                  {KPI_CARDS.map((card) => (
                    <div
                      key={card.id}
                      className="h-[88px] animate-pulse rounded-2xl border border-slate-200 bg-slate-50"
                    />
                  ))}
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
                  {KPI_CARDS.map((card) => {
                    const Icon = card.icon;
                    const value = kpis[card.id] ?? 0;
                    return (
                      <Link
                        key={card.id}
                        to={card.href}
                        className="group block rounded-2xl border border-slate-200 bg-white px-3 py-3 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md"
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div
                            className={`flex h-8 w-8 items-center justify-center rounded-lg ${card.iconWrap}`}
                          >
                            <Icon className="h-4 w-4" />
                          </div>
                          {card.info ? (
                            <CalculationInfoIcon
                              ariaLabel={t(`hse.dashboard.kpi.${card.id}.info_aria`)}
                              onClick={(e) => {
                                e.preventDefault();
                                setInfoKpi(card.id);
                              }}
                            />
                          ) : (
                            <ArrowRightIcon className="mt-0.5 h-3.5 w-3.5 text-slate-400 transition group-hover:translate-x-0.5" />
                          )}
                        </div>
                        <div className="mt-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">
                          {t(`hse.dashboard.kpi.${card.id}`)}
                        </div>
                        <div className="mt-0.5 text-xl font-semibold tabular-nums text-slate-950">
                          {value}
                        </div>
                      </Link>
                    );
                  })}
                </div>
              )}
            </section>

            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
              <SectionCard
                title={t("hse.dashboard.tasks.title")}
                action={
                  <SeeAllLink
                    to="/dashboard/quality/employee-handbook"
                    label={t("hse.dashboard.see_all")}
                  />
                }
              >
                {loading ? (
                  <ResourceLoading size="small" />
                ) : dashboard.my_tasks.length === 0 ? (
                  <EmptyLine text={t("hse.dashboard.tasks.empty")} />
                ) : (
                  <ul className="divide-y divide-slate-100">
                    {dashboard.my_tasks.map((task) => (
                      <li key={task.id}>
                        <Link
                          to={task.href}
                          className="flex items-start justify-between gap-3 py-3 hover:bg-slate-50"
                        >
                          <div className="min-w-0">
                            <div className="text-sm font-medium text-slate-950">
                              {task.title}
                            </div>
                            <div className="mt-0.5 flex flex-wrap items-center gap-x-2 text-xs text-slate-500">
                              <span>
                                {t(`hse.dashboard.tasks.kind.${task.kind}`)}
                              </span>
                              {task.due_date ? (
                                <span>
                                  {t("hse.dashboard.tasks.due", {
                                    date: formatDate(task.due_date, i18n.language),
                                  })}
                                </span>
                              ) : null}
                              {task.subtitle ? <span>{task.subtitle}</span> : null}
                            </div>
                          </div>
                          {task.overdue ? (
                            <span className="shrink-0 rounded-full bg-rose-50 px-2 py-0.5 text-[11px] font-medium text-rose-700">
                              {t("hse.dashboard.tasks.overdue")}
                            </span>
                          ) : null}
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </SectionCard>

              <SectionCard title={t("hse.dashboard.alerts.title")}>
                {loading ? (
                  <ResourceLoading size="small" />
                ) : dashboard.alerts.length === 0 ? (
                  <EmptyLine text={t("hse.dashboard.alerts.empty")} />
                ) : (
                  <ul className="space-y-2">
                    {dashboard.alerts.map((alert) => {
                      const body = alertLabel(alert);
                      const className = `block rounded-xl border px-3 py-2.5 text-sm ${ALERT_STYLES[alert.severity] || ALERT_STYLES.info}`;
                      if (alert.href) {
                        return (
                          <li key={alert.id}>
                            <Link to={alert.href} className={className}>
                              <span className="flex items-start gap-2">
                                <BellAlertIcon className="mt-0.5 h-4 w-4 shrink-0" />
                                <span>
                                  <span className="font-medium">{body}</span>
                                  {alert.body ? (
                                    <span className="mt-0.5 block text-xs opacity-80">
                                      {alert.body}
                                    </span>
                                  ) : null}
                                </span>
                              </span>
                            </Link>
                          </li>
                        );
                      }
                      return (
                        <li key={alert.id} className={className}>
                          <span className="flex items-start gap-2">
                            <BellAlertIcon className="mt-0.5 h-4 w-4 shrink-0" />
                            <span>
                              <span className="font-medium">{body}</span>
                              {alert.body ? (
                                <span className="mt-0.5 block text-xs opacity-80">
                                  {alert.body}
                                </span>
                              ) : null}
                            </span>
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </SectionCard>
            </div>

            <SectionCard
              title={t("hse.dashboard.events.title")}
              action={
                <SeeAllLink
                  to="/dashboard/hse-ks/events"
                  label={t("hse.dashboard.events.view_all")}
                />
              }
            >
              {loading ? (
                <ResourceLoading size="small" />
              ) : dashboard.recent_events.length === 0 ? (
                <EmptyLine text={t("hse.dashboard.events.empty")} />
              ) : (
                <ul className="divide-y divide-slate-100">
                  {dashboard.recent_events.map((event) => (
                    <li key={`${event.kind || "hse_event"}-${event.id}`}>
                      <Link
                        to={event.href}
                        className="-mx-1 flex items-center justify-between gap-3 rounded-lg px-1 py-3 hover:bg-slate-50"
                      >
                        <div className="min-w-0">
                          <div className="truncate text-sm font-medium text-slate-950">
                            {event.title}
                          </div>
                          <div className="mt-0.5 flex flex-wrap gap-x-3 text-xs text-slate-500">
                            {event.event_type ? <span>{event.event_type}</span> : null}
                            {event.status ? <span>{event.status}</span> : null}
                          </div>
                        </div>
                        <div className="shrink-0 text-xs tabular-nums text-slate-500">
                          {formatDate(event.event_date, i18n.language)}
                        </div>
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </SectionCard>

            <SectionCard
              title={
                <span className="inline-flex items-center">
                  <BuildingOffice2Icon className="mr-2 h-5 w-5 text-blue-600" />
                  {t("hse.dashboard.org_charts.title")}
                </span>
              }
              action={
                <SeeAllLink
                  to="/dashboard/hse-ks/org-charts"
                  label={t("hse.dashboard.org_charts.view_all")}
                />
              }
            >
              {loadingOrgCharts ? (
                <ResourceLoading size="small" />
              ) : approvedCharts.length === 0 ? (
                <EmptyLine text={t("hse.dashboard.org_charts.empty")} />
              ) : (
                <div className="space-y-3">
                  {approvedCharts.map((orgChart) => (
                    <div
                      key={orgChart.id}
                      className="rounded-xl border border-slate-200 p-4 hover:bg-slate-50"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <div className="mb-1 flex items-center gap-2">
                            <DocumentTextIcon className="h-5 w-5 shrink-0 text-blue-600" />
                            <h3 className="truncate text-sm font-medium text-slate-950">
                              {orgChart.title}
                            </h3>
                            {orgChart.approved_revision ? (
                              <CheckCircleIcon className="h-4 w-4 shrink-0 text-emerald-500" />
                            ) : null}
                          </div>
                          {orgChart.description ? (
                            <p className="mb-2 text-sm text-slate-500">{orgChart.description}</p>
                          ) : null}
                          {orgChart.approved_revision ? (
                            <div className="flex flex-wrap gap-x-4 text-xs text-slate-500">
                              <span>
                                {t("hse.dashboard.org_charts.revision", {
                                  number: orgChart.approved_revision.revision_number,
                                })}
                              </span>
                              {orgChart.approved_revision.approved_at ? (
                                <span>
                                  {t("hse.dashboard.org_charts.approved", {
                                    date: formatDate(
                                      orgChart.approved_revision.approved_at,
                                      i18n.language
                                    ),
                                  })}
                                </span>
                              ) : null}
                            </div>
                          ) : null}
                        </div>
                        <div className="flex shrink-0 items-center gap-2">
                          <Link
                            to={`/dashboard/hse-ks/org-charts/${orgChart.id}`}
                            className="inline-flex items-center rounded border border-slate-300 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
                          >
                            <EyeIcon className="mr-1 h-4 w-4" />
                            {t("hse.dashboard.org_charts.view")}
                          </Link>
                          {orgChart.approved_revision ? (
                            <button
                              type="button"
                              onClick={() =>
                                handleExportPDF(orgChart.id, orgChart.approved_revision.id)
                              }
                              className="inline-flex items-center rounded border border-slate-300 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
                            >
                              <PrinterIcon className="mr-1 h-4 w-4" />
                              {t("hse.dashboard.org_charts.pdf")}
                            </button>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </SectionCard>
          </div>
        )}
      </div>

      <PMDialog
        open={Boolean(infoKpi)}
        onClose={() => setInfoKpi(null)}
        title={infoKpi ? t(`hse.dashboard.kpi.${infoKpi}`) : ""}
        size="md"
      >
        <p className="text-sm text-slate-600">
          {infoKpi ? t(`hse.dashboard.kpi.${infoKpi}.info`) : ""}
        </p>
      </PMDialog>
    </HsePageChrome>
  );
};
